import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { MailboxFolder, folderDisplayName, folderShowsUnread } from '../_types/mailbox';

/// Árbol de carpetas del buzón.
///
/// Usa `p-tree` en lugar de una lista propia porque IMAP es jerárquico de verdad (con separador de nivel, que no
/// es el mismo en todos los servidores) y `p-tree` ya resuelve desplegar y plegar con estado, la selección y la
/// plantilla de nodo para el contador de no leídos. Reimplementar todo eso a mano no aportaría nada.
///
/// Los nombres se muestran traducidos (`folderDisplayName`): los servidores devuelven el nombre canónico en inglés
/// para las carpetas de siempre, y en el caso de INBOX el nombre es parte del protocolo. La ruta, que es el
/// identificador, no se toca nunca.
///
/// Los nodos desplegados se recuerdan en el almacenamiento local por cuenta: cambiar de carpeta y volver no debe
/// replegar el árbol.
@Component({
    selector: 'app-folder-tree',
    templateUrl: './folder-tree.component.html',
    styleUrls: ['./folder-tree.component.css'],
    standalone: false
})
export class FolderTreeComponent implements OnChanges {
  @Input() folders: MailboxFolder[] = [];
  @Input() selectedPath: string | null = null;
  @Input() mailAccountId: string | null = null;
  @Input() loading: boolean = false;

  @Output() folderSelected = new EventEmitter<MailboxFolder>();

  public nodes: TreeNode<MailboxFolder>[] = [];
  public selectedNode: TreeNode<MailboxFolder> | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['folders']) this.nodes = this.toNodes(this.folders);
    if (changes['selectedPath'] || changes['folders']) this.syncSelection();
  }

  onSelect(event: any) {
    const folder: MailboxFolder | undefined = event?.node?.data;
    if (!folder) return;

    //hay servidores con carpetas que solo agrupan a otras y no se pueden abrir: pulsarlas no debe intentar listar
    if (!folder.selectable) return;

    this.folderSelected.emit(folder);
  }

  onExpandedChange() {
    this.persistExpanded();
  }

  /// Icono según el papel de la carpeta, para que las de siempre se reconozcan de un vistazo.
  iconFor(folder: MailboxFolder): string {
    switch (folder.specialUse) {
      case 'inbox': return 'pi pi-inbox';
      case 'sent': return 'pi pi-send';
      case 'drafts': return 'pi pi-file-edit';
      case 'trash': return 'pi pi-trash';
      case 'junk': return 'pi pi-ban';
      case 'archive': return 'pi pi-box';
      default: return 'pi pi-folder';
    }
  }

  /// Sin leer que se muestran para la carpeta: cero donde el dato no significa nada. Ver `folderShowsUnread`.
  unreadOf(folder: MailboxFolder): number {
    return folderShowsUnread(folder) ? (folder.unread ?? 0) : 0;
  }

  private toNodes(folders: MailboxFolder[]): TreeNode<MailboxFolder>[] {
    const expanded = this.readExpanded();

    const map = (folder: MailboxFolder): TreeNode<MailboxFolder> => ({
      key: folder.path,
      label: folderDisplayName(folder.name, folder.specialUse),
      data: folder,
      selectable: folder.selectable,
      expanded: expanded.has(folder.path) || folder.specialUse === 'inbox',
      children: (folder.children ?? []).map(map),
      leaf: (folder.children ?? []).length === 0
    });

    return (folders ?? []).map(map);
  }

  private syncSelection() {
    this.selectedNode = this.selectedPath ? this.findNode(this.nodes, this.selectedPath) : null;

    //si la carpeta seleccionada está enterrada, se despliegan sus ancestros para que se vea
    if (this.selectedNode) this.expandAncestors(this.nodes, this.selectedPath!);
  }

  private findNode(nodes: TreeNode<MailboxFolder>[], path: string): TreeNode<MailboxFolder> | null {
    for (const node of nodes) {
      if (node.key === path) return node;
      const found = this.findNode(node.children ?? [], path);
      if (found) return found;
    }
    return null;
  }

  private expandAncestors(nodes: TreeNode<MailboxFolder>[], path: string): boolean {
    for (const node of nodes) {
      if (node.key === path) return true;
      if (this.expandAncestors(node.children ?? [], path)) {
        node.expanded = true;
        return true;
      }
    }
    return false;
  }

  private storageKey(): string {
    return `mailbox.expandedFolders.${this.mailAccountId ?? 'default'}`;
  }

  private readExpanded(): Set<string> {
    try {
      const raw = localStorage.getItem(this.storageKey());
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  }

  private persistExpanded() {
    const expanded: string[] = [];

    const walk = (nodes: TreeNode<MailboxFolder>[]) => {
      for (const node of nodes) {
        if (node.expanded && node.key) expanded.push(node.key);
        walk(node.children ?? []);
      }
    };
    walk(this.nodes);

    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(expanded));
    } catch {
      //si no se puede guardar la preferencia, no pasa nada: el árbol arranca plegado
    }
  }
}
